import { Checkbox } from "@base-ui-components/react/checkbox";
import { CheckboxGroup } from "@base-ui-components/react/checkbox-group";
import type { ComponentProps } from "react";
import { Icon } from "~components/ui";
import { Button } from "./button";
import {
  type EventFilterKeys,
  useFilterEvents,
} from "./filter-events-context-provider";
import { useSectionEventTranslations } from "./translations-section-events-context-provider";

interface EventsFilterListProps {
  type: EventFilterKeys;
  title: string;
  list: string[];
}

function CheckIcon(props: Readonly<ComponentProps<"svg">>) {
  return (
    <svg
      fill="currentcolor"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      {...props}
    >
      <title>check mark icon</title>
      <path d="M9.1603 1.12218C9.50684 1.34873 9.60427 1.81354 9.37792 2.16038L5.13603 8.66012C5.01614 8.8438 4.82192 8.96576 4.60451 8.99384C4.3871 9.02194 4.1683 8.95335 4.00574 8.80615L1.24664 6.30769C0.939709 6.02975 0.916013 5.55541 1.19372 5.24822C1.47142 4.94102 1.94536 4.91731 2.2523 5.19524L4.36085 7.10461L8.12299 1.33999C8.34934 0.993152 8.81376 0.895638 9.1603 1.12218Z" />
    </svg>
  );
}

function EventsFilterList({
  title,
  list,
  type,
}: Readonly<EventsFilterListProps>) {
  const { t } = useSectionEventTranslations();
  const { selectedFilters, toggleFilter, clearFilterByType } =
    useFilterEvents();
  const selectedValuesSet = selectedFilters[type];
  const selectedValuesArray = Array.from(selectedValuesSet);

  const hasFilter = !!selectedValuesArray.length;
  return (
    <div className="flex flex-col gap-xs px-xs py-sm">
      <div className="flex items-center justify-between">
        <h3 className="t-default-md font-bold">{title}</h3>
        {hasFilter && (
          <Button
            className="t-default-xs flex items-center gap-[2px] text-secondaryButton-text underline"
            onClick={() => clearFilterByType(type)}
          >
            <Icon weight="bold" size="12" name="X" />
            <span>{t.clear}</span>
          </Button>
        )}
      </div>

      <CheckboxGroup
        aria-labelledby={title}
        className="flex flex-col items-start gap-[2px]"
        onValueChange={value => toggleFilter(type, value)}
        value={selectedValuesArray}
      >
        {list.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          <label key={index} className="flex items-center gap-3xs">
            <Checkbox.Root
              name={title}
              value={item}
              className="flex size-[20px] items-center justify-center rounded-[4px] data-[unchecked]:border data-[unchecked]:border-gray-300 data-[checked]:bg-satellite-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800 focus-visible:outline-offset-2"
            >
              <Checkbox.Indicator className="flex text-others-white data-[unchecked]:hidden">
                <CheckIcon className="size-[14px]" />
              </Checkbox.Indicator>
            </Checkbox.Root>
            <span>{item}</span>
          </label>
        ))}
      </CheckboxGroup>
    </div>
  );
}

export { EventsFilterList };
